// D41-C4 chat card for a pending service_grant approval: what the agent asks to do (action, resource, its reason, effect,
// duration and limit, undo) and one button per answer the server offers. Every label is i18n; the agent's reason is user
// content, rendered as text and never as markup. Only an organization administrator can answer (the API enforces it too).
import { useId, useState, type ReactNode } from "react";
import { GRANT_OPTIONS, TYPED_OPERATIONS, type GrantEffectKey, type GrantOptionKey, type GrantReversalKey, type ServiceGrantKind, type TypedOperationType } from "@maestros/contracts";
import { useI18n } from "../../i18n/index.tsx";
import { decideApproval, listApprovals } from "../../api/endpoints-wave5.ts";
import type { Approval } from "../../api/types-wave5.ts";
import { useAsync } from "../../lib/useAsync.ts";
import { useOrgRole } from "../config/shared.tsx";
import { Button } from "../../components/Button.tsx";
import { ActionError } from "../../components/ActionError.tsx";

/** The fields each typed operation carries (the resolved targets the server stores in the request's action). */
const FIELDS: Record<TypedOperationType, string[]> = {
  "service.restart": ["app"],
  "service.redeploy": ["app", "commit_sha"],
  "database.provision": ["coolify_project", "environment", "name"],
  "env.set_secret_ref": ["app", "key"],
};

/** The resource in the form migration 0040 checks (display only: the server computes the same text). */
const RESOURCE_OF: Record<TypedOperationType, (o: Record<string, string>) => string> = {
  "service.restart": (o) => `coolify:app:${o["app"]}`,
  "service.redeploy": (o) => `coolify:app:${o["app"]}`,
  "database.provision": (o) => `coolify:environment:${o["coolify_project"]}/${o["environment"]}`,
  "env.set_secret_ref": (o) => `coolify:app:${o["app"]}/${o["key"]}`,
};

export interface GrantFacts {
  type: TypedOperationType;
  /** Values the action sentence needs (commit, database name, variable key). */
  params: Record<string, string>;
  resource: string;
  reason: string;
  effect: GrantEffectKey;
  reversal: GrantReversalKey;
  /** The grant kinds offered by the answers, in the order of the answers ("deny" excluded). */
  kinds: ServiceGrantKind[];
  /** The project secret an env.set_secret_ref names: its name and masked hint, never its value. */
  secret: { name: string; maskedHint: string } | null;
}

export interface GrantOffer {
  key: string;
  choice: ServiceGrantKind | "deny";
}

/** The facts of a service_grant request, or null when its action is not a readable typed operation. */
export function grantFacts(approval: Approval): GrantFacts | null {
  const operation = approval.action["operation"];
  if (typeof operation !== "object" || operation === null) return null;
  const o = operation as Record<string, unknown>;
  const type = typeof o["type"] === "string" && Object.hasOwn(TYPED_OPERATIONS, o["type"]) ? (o["type"] as TypedOperationType) : null;
  if (type === null) return null;
  const values: Record<string, string> = {};
  for (const field of FIELDS[type]) {
    const value = o[field];
    if (typeof value !== "string" || value === "") return null;
    values[field] = value;
  }
  const spec = TYPED_OPERATIONS[type];
  const offers = grantOffers(approval, type);
  const secret = approval.action["secret"] as { name?: unknown; masked_hint?: unknown } | undefined;
  return {
    type,
    params: values,
    resource: RESOURCE_OF[type](values),
    reason: typeof approval.action["reason"] === "string" ? (approval.action["reason"] as string) : "",
    effect: spec.effectKey,
    reversal: spec.reversalKey,
    kinds: offers.flatMap((offer) => (offer.choice === "deny" ? [] : [offer.choice])),
    secret: typeof secret?.name === "string" && typeof secret.masked_hint === "string" ? { name: secret.name, maskedHint: secret.masked_hint } : null,
  };
}

/** The answers the server offers that this operation allows: a known key, and a grant kind the operation permits. */
export function grantOffers(approval: Approval, type: TypedOperationType): GrantOffer[] {
  const allowed = TYPED_OPERATIONS[type].grantKinds;
  return approval.options.flatMap((option) => {
    const choice = Object.hasOwn(GRANT_OPTIONS, option.key) ? GRANT_OPTIONS[option.key as GrantOptionKey] : undefined;
    if (choice === undefined) return [];
    if (choice !== "deny" && !allowed.includes(choice)) return [];
    return [{ key: option.key, choice }];
  });
}

/** The facts of a grant request, for the chat card and the Kanban decision (same text in both). */
export function GrantFactsList({ approval }: { approval: Approval }) {
  const { t } = useI18n();
  const facts = grantFacts(approval);
  if (facts === null) return <p className="assistant-note">{t("grant.unreadable")}</p>;
  return (
    <dl className="grant-facts" data-testid="grant-facts">
      <dt>{t("grant.field.action")}</dt>
      <dd>{t(`grant.action.${facts.type}`, facts.params)}</dd>
      <dt>{t("grant.field.resource")}</dt>
      <dd>
        <code className="user-content">{facts.resource}</code>
      </dd>
      {facts.secret !== null ? (
        <>
          <dt>{t("grant.field.secret")}</dt>
          <dd className="user-content" data-testid="grant-secret">
            {facts.secret.name} <code>{facts.secret.maskedHint}</code>
          </dd>
        </>
      ) : null}
      <dt>{t("grant.field.reason")}</dt>
      <dd className="user-content" data-testid="grant-reason">
        {facts.reason}
      </dd>
      <dt>{t("grant.field.effect")}</dt>
      <dd>{t(`grant.effect.${facts.effect}`)}</dd>
      <dt>{t("grant.field.terms")}</dt>
      <dd>
        <ul>
          {facts.kinds.map((kind) => (
            <li key={kind}>{t(`grant.term.${kind}`)}</li>
          ))}
        </ul>
      </dd>
      <dt>{t("grant.field.reversal")}</dt>
      <dd>{t(`grant.reversal.${facts.reversal}`)}</dd>
    </dl>
  );
}

export function GrantApprovalCard({ approval, isAdmin }: { approval: Approval; isAdmin: boolean }) {
  const { t } = useI18n();
  const titleId = useId();
  const [busy, setBusy] = useState<string | null>(null);
  const [decided, setDecided] = useState<GrantOffer["choice"] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const facts = grantFacts(approval);
  const offers = facts === null ? [] : grantOffers(approval, facts.type);

  const decide = async (key: string, choice: GrantOffer["choice"]) => {
    setBusy(key);
    setError(null);
    try {
      await decideApproval(approval.id, { option: key });
      setDecided(choice);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="assistant-action grant-card" aria-labelledby={titleId} data-testid="grant-approval">
      <h4 id={titleId} className="grant-title">
        {t("grant.title")}
      </h4>
      <GrantFactsList approval={approval} />
      {facts !== null && decided === null ? (
        <>
          {!isAdmin ? <p className="assistant-note" data-testid="grant-admin-only">{t("grant.adminOnly")}</p> : null}
          <div className="assistant-action-buttons" role="group" aria-label={t("grant.choose")}>
            {offers.map((offer) => (
              <Button
                key={offer.key}
                size="sm"
                variant={offer.choice === "deny" ? "ghost" : "secondary"}
                disabled={!isAdmin || busy !== null}
                loading={busy === offer.key}
                onClick={() => void decide(offer.key, offer.choice)}
                data-testid="grant-option"
                data-choice={offer.choice}
              >
                {t(`grant.option.${offer.choice}`)}
              </Button>
            ))}
          </div>
        </>
      ) : null}
      <ActionError error={error} onDismiss={() => setError(null)} />
      <p className="assistant-note" aria-live="polite" data-testid="grant-result">
        {decided !== null ? t("grant.decided", { option: t(`grant.option.${decided}`) }) : null}
      </p>
    </section>
  );
}

/**
 * A decide_approval action of the chat: the grant card when its approval is a pending service_grant (its generic Run
 * button is then hidden), otherwise the generic card. While the approval is loading, and whenever its kind cannot be
 * read (the list failed or does not contain it), nothing is shown: the generic Run button never answers a grant.
 */
export function DecisionAction({ approvalId, generic }: { approvalId: string; generic: ReactNode }) {
  const { atLeast } = useOrgRole();
  const pending = useAsync((signal) => listApprovals("pending", signal), [approvalId]);
  if (pending.loading) return null;
  const approval = (pending.data?.items ?? []).find((a) => a.id === approvalId);
  if (approval === undefined) return null;
  if (approval.kind === "service_grant") return <GrantApprovalCard approval={approval} isAdmin={atLeast("admin")} />;
  return <>{generic}</>;
}
